<template>
  <div data-view="billing-account-network-form">
    <form @:submit.prevent="onSubmit">
      <section class="add-network">
        <h5 class="mb-2">
          {{ $t('add_network') }}
        </h5>

        <v-row>
          <v-text-field
            v-model="networkName"
            :label="$t('network_name')"
            :error-messages="networkNameErrors"
          />
        </v-row>

        <v-row>
          <v-text-field
            v-model="financeContact"
            :label="$t('finance_contact')"
            :error-messages="financeContactErrors"
          />
        </v-row>
      </section>

      <LinkToForm
        method="network"
        :billing-account="item"
        :link-countries="linkCountries"
      />

      <v-row>
        <div v-if="hasFormErrors" xs12 class="form-error-field">
          <v-icon color="white" size="13px"> warning </v-icon>
          <span>
            {{ $t('to_continue_please_check') }}
          </span>
        </div>
      </v-row>

      <v-row>
        <div>
          <div class="network-actions">
            <v-btn
              :disabled="isLoading"
              variant="text"
              @click="onCancelClick"
            >
              {{ $t('cancel') }}
            </v-btn>

            <v-btn
              :class="submitClass"
              :disabled="isLoading"
              color="primary"
              elevation="0"
              @click="onSubmit"
            >
              <v-progress-circular
                v-if="isLoading"
                class="spinner"
                color="white"
                indeterminate
                :width="2"
                :size="12"
              />

              {{ triggerLabel }}
            </v-btn>
          </div>
        </div>
      </v-row>
    </form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { isNum } from '../../../utils';
  import LinkToForm from './LinkTo';

  export default {
    components: {
      LinkToForm,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        networkName: {
          required,
        },
        financeContact: {
          required,
          email: {
            $validator: value => {
              const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
              return pattern.test(value);
            },
            $message: 'Must be a valid email address'
          }
        },
      };
    },
    props: {
      item: {
        type: Object,
        required: true,
      },
      onCancel: {
        type: Function,
        required: true,
      },
    },
    data() {
      return {
        networkName: '',
        financeContact: '',
        isLoading: false,
        submitClass: 'submit-btn'
      };
    },
    computed: {
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      triggerLabel() {
        if (this.isLoading) {
          return this.$t('loading');
        }

        return this.item.id
          ? this.$t('update_billing_account')
          : this.$t('add_billing_account');
      },
      hasFormErrors() {
        return this.networkNameErrors.length || this.financeContactErrors.length;
      },
      networkNameErrors() {
        return this.v$.networkName.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('network_name_is_required');
          }
          return error.$message;
        });
      },
      financeContactErrors() {
        return this.v$.financeContact.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('finance_contact_is_required');
          }
          if (error.$validator === 'email') {
            return this.$t('must_be_a_valid_email_address');
          }
          return error.$message;
        });
      },
    },
    created() {
      this.submitClass = isNum(this.item?.id) ? 'update-btn' : 'submit-btn';
      this.extractNetworkItem(this.item);
    },
    methods: {
      onCancelClick() {
        this.networkName = '';
        this.financeContact = '';
        this.v$.$reset();
        this.onCancel();
      },
      getBillingSettings() {
        return this.$store.dispatch('businesses/getBillingSettings');
      },
      extractNetworkItem(item) {
        if (item && item.id) {
          this.networkName = item.name;
          this.financeContact = item.finance_contact;
          this.linkedCountries = item.linked_countries || [];
          return;
        }

        this.networkName = '';
        this.financeContact = '';
      },
      linkCountries(items) {
        this.linkedCountries = items;
      },
      canSubmitRequest() {
        return (
          this.activeUser.business_id &&
          this.networkName &&
          this.financeContact &&
          !this.hasFormErrors
        );
      },
      onSubmit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.isLoading = true;

        return this.item.id ? this.updateNetwork() : this.createNetwork();
      },
      createNetwork() {
        const {
          item,
          networkName,
          financeContact,
          linkedCountries,
          getBillingSettings,
          onCancel,
        } = this;
        const query = {
          country: item.country,
          name: networkName,
          finance_contact: financeContact,
          linked_countries:
            linkedCountries && linkedCountries.length
              ? linkedCountries.map((c) => c.code)
              : [],
        };

        return this.$store
          .dispatch('networks/createItem', query)
          .then((res) => {
            this.isLoading = false;
            if (res.data) {
              getBillingSettings();
              onCancel();
            }
            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      updateNetwork() {
        const {
          item,
          networkName,
          financeContact,
          linkedCountries,
          getBillingSettings,
          onCancel,
        } = this;
        const query = {
          network_id: item.id,
          country: item.country,
          name: networkName,
          finance_contact: financeContact,
          linked_countries:
            linkedCountries && linkedCountries.length
              ? linkedCountries.map((c) => c.code)
              : [],
        };

        return this.$store
          .dispatch('networks/updateItem', query)
          .then((res) => {
            this.isLoading = false;
            if (res.data) {
              getBillingSettings();
              onCancel();
            }
            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
    },
  };
</script>

<style lang="scss">
[data-view='billing-account-network-form'] {
  margin-top: 24px;
  min-height: auto;

  .add-network {
    padding: 0 16px;

    h5 {
      font-size: 16px;
      color: #424242;

      .v-theme--dark & {
        color: rgb(var(--v-theme-on-surface));
      }
    }
  }

  .v-alert {
    font-size: 16px;
    margin: 4px 0 24px 0;

    .v-icon {
      margin-left: 8px;
    }

    span {
      color: #f57c00;
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .info-box {
    margin-bottom: 24px;
    padding: 8px 16px;
    color: #424242;
    font-size: 12px;
    line-height: 16px;
    background: rgba(25, 118, 210, 0.07);
    border-radius: 2px;

    .v-theme--dark & {
      color: rgb(var(--v-theme-on-surface));
    }
  }

  .form-error-field {
    display: flex;
    margin: 20px 8px 0 8px;
    color: white;
    font-size: 14px;
    font-weight: 400;
    background: rgb(var(--v-theme-error));
    border-radius: 2px;

    .v-icon {
      margin-right: 4px;
    }
  }

  .network-actions {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;

    .v-btn {
      border-radius: 4px;
    }

    .submit-btn,
    .update-btn {
      margin-left: 16px;
      width: 158px;

      .spinner {
        margin-right: 4px;
      }

      &:disabled {
        color: white !important;
      }
    }

    .update-btn {
      width: 180px;
    }
  }
}
</style>
